import { useState } from "react";
import { api, type CarouselItem, type EpidemicStatus, type EpidemicTrack } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StudioOptionCard } from "@/components/DesignReviewPanel";
import { CtaIo } from "@/components/studioTabs";
import { cn } from "@/lib/utils";

type Props = {
  item: CarouselItem;
  music?: EpidemicStatus;
  ffmpeg?: boolean;
  tracks: EpidemicTrack[];
  term?: string;
  loading?: boolean;
  searchPending?: boolean;
  composePending?: boolean;
  onSearch: (q: string) => void;
  onCompose: (trackId: string) => void;
};

export default function CarouselReelPanel({
  item,
  music,
  ffmpeg = true,
  tracks,
  term,
  loading,
  searchPending,
  composePending,
  onSearch,
  onCompose,
}: Props) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(item.reel_track_id || "");
  const selected = tracks.find((t) => t.id === picked) || tracks.find((t) => t.id === item.reel_track_id);
  const reelBusy = composePending || item.reel_status === "queued" || item.reel_status === "rendering";
  const canMux = Boolean(selected && !selected.is_preview_only && music?.can_download !== false);
  const previewSrc = selected ? api.carouselMusicPreview(selected.id) : "";

  return (
    <StudioOptionCard
      title="Rolka na FB"
      hint="FB dostaje rolkę 9:16 z muzyką Epidemic. IG zostaje karuzelą slajdów."
    >
      <p className="text-xs text-muted-foreground">
        FB = rolka. IG = karuzela. Muzyka musi iść w pliku MP4, Graph nie dokleja biblioteki Meta.
      </p>
      {music?.hint ? <p className="text-xs text-warning">{music.hint}</p> : null}
      {!ffmpeg ? <p className="text-xs text-warning">Na serwerze nie ma ffmpeg. Rolki nie złożę.</p> : null}
      <div className="flex flex-wrap gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="np. warm calm piano"
          className="max-w-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch(q);
          }}
        />
        <Button size="sm" variant="outline" disabled={searchPending || !q.trim()} onClick={() => onSearch(q)}>
          {searchPending ? "Szukam…" : "Szukaj"}
        </Button>
      </div>
      {term ? <p className="text-[11px] text-muted-foreground">Query: {term}</p> : null}
      {loading ? <p className="text-xs text-muted-foreground">Propozycje z briefu…</p> : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {tracks.map((track) => (
          <button
            key={track.id}
            type="button"
            onClick={() => setPicked(track.id)}
            className={cn(
              "rounded-md border p-2 text-left text-xs",
              picked === track.id ? "border-brand bg-brand/5" : "border-border",
            )}
          >
            <div className="font-medium text-foreground">{track.title || track.id}</div>
            <div className="text-muted-foreground">{track.artist || "Epidemic"}</div>
            {track.is_preview_only ? (
              <div className="mt-1 text-warning">preview-only, nie na FB</div>
            ) : null}
          </button>
        ))}
      </div>
      {previewSrc && selected && !selected.is_preview_only ? (
        <audio className="w-full" controls src={previewSrc} preload="none" />
      ) : null}
      <Button
        size="sm"
        variant="brand"
        disabled={!item.id || !canMux || !ffmpeg || reelBusy || !selected}
        onClick={() => selected && onCompose(selected.id)}
      >
        {reelBusy ? "Składam rolkę…" : "Złóż rolkę"}
      </Button>
      <CtaIo io="reel" />
      {item.reel_error ? <p className="text-sm text-destructive">{item.reel_error}</p> : null}
      {item.has_reel ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {item.reel_track_title || "Rolka gotowa"}
            {item.epidemic_license_ref ? ` · ${item.epidemic_license_ref}` : ""}
          </p>
          <video
            className="aspect-[9/16] w-40 rounded border border-border bg-muted"
            controls
            src={`${api.carouselReelUrl(item.id)}?v=${item.updated_at || item.reel_status || ""}`}
          />
        </div>
      ) : null}
    </StudioOptionCard>
  );
}
